import { useTracking } from '~/composables/custom/useTracking';
import { useUsercentrics } from '~/composables/custom/useUsercentrics/useUsercentrics';

// From: https://docs.usercentrics.com/#/v2-events?id=uc_ui_cmp_event
export enum UC_UI_CMP_EVENT {
  ON_SHOW = 'CMP_SHOW',
  ON_ACCEPT_ALL = 'ACCEPT_ALL',
  ON_DENY_ALL = 'DENY_ALL',
  ON_SAVE = 'SAVE',
  MORE_INFORMATION_LINK = 'MORE_INFORMATION_LINK',
  IMPRINT_LINK = 'IMPRINT_LINK',
  PRIVACY_POLICY_LINK = 'PRIVACY_POLICY_LINK',
}

export enum UC_EVENTS {
  UI_INITIALIZED = 'UC_UI_INITIALIZED',
  UI_CMP_EVENT = 'UC_UI_CMP_EVENT',
  ON_PRIVACY_CLICK = 'onUcPrivacyClick',
  ON_IMPRINT_CLICK = 'onUcImprintClick',
}

const clearMarketingSessionStorage = [
  'tr_geo',
] as const;

const clearMarketingLocalStorage = [
  'tr_geo',
] as const;

const clearFunctionalCookies = [
  'wtsid',
  'wteid',
  'wtstp_',
] as const;

type CookiesType = typeof clearFunctionalCookies;

const clearSessionStorage = (storageNames: typeof clearMarketingSessionStorage) => storageNames.forEach(item => sessionStorage.removeItem(item));
const clearLocalStorage = (storageNames: typeof clearMarketingLocalStorage) => storageNames.forEach(item => localStorage.removeItem(item));
const getAllCookiesKeys = () => document.cookie.split(';').map(cookie => cookie.split('=')?.[0]);

export default defineNuxtPlugin({
  name: 'cookieConsent',
  dependsOn: ['gtm'],
  async setup() {
    const { $i18n, $domain } = useNuxtApp();
    const { gtmTracking, trackNavigation } = useTracking();
    const router = useRouter();
    const { isFunctionalConsentGiven, isMarketingConsentGiven } = useUsercentrics();

    const afterEachHook = ref();

    const clearCookies = (cookiePrefixes: CookiesType) => {
      getAllCookiesKeys().forEach((cookieName) => {
        const match = cookiePrefixes.some(prefix => cookieName.startsWith(prefix));
        if (match) {
          useCookie(cookieName, { path: '/', domain: `.kik.${$domain.countryCode.toLocaleLowerCase()}` }).value = undefined;
        }
      });
    };

    const handleEvents = (event: { detail: { type: UC_UI_CMP_EVENT } }) => {
      const { type } = event.detail;

      if (type === UC_UI_CMP_EVENT.ON_SAVE || type === UC_UI_CMP_EVENT.ON_ACCEPT_ALL) {
        const marketing = isMarketingConsentGiven();
        const functional = isFunctionalConsentGiven();

        gtmTracking({ event: 'consentChange', marketing, functional });

        if (!marketing) {
          clearLocalStorage(clearMarketingLocalStorage);
          clearSessionStorage(clearMarketingSessionStorage);
        }

        if (!functional) {
          clearCookies(clearFunctionalCookies);
        }
      }
    };

    const showPrivacy = () => {
      window.open($i18n.t('https://legal.kik.{countryCode}/privacy/', { countryCode: $domain.countryCode }).toLowerCase(), '_blank', 'noreferrer,noopener');
    };
    const showImprint = () => {
      window.open($i18n.t('https://legal.kik.{countryCode}/imprint/', { countryCode: $domain.countryCode }).toLowerCase(), '_blank', 'noreferrer,noopener');
    };

    window.addEventListener(UC_EVENTS.UI_CMP_EVENT, handleEvents);
    window.addEventListener(UC_EVENTS.ON_PRIVACY_CLICK, showPrivacy);
    window.addEventListener(UC_EVENTS.ON_IMPRINT_CLICK, showImprint);

    afterEachHook.value = router.beforeEach(trackNavigation);
  },
});
